import { useMemo } from "react";

import { Group, UnstyledButton } from "@mantine/core";
import { useTranslation } from "react-i18next";


import { Select } from "components";

import classes from "./kpi.module.css";
import { getMetricLabelKey, KPI_METRIC_ORDER } from "./kpiCatalog";

interface KpiMetricSelectorProps {
  metric: string;
  onMetricChange: (metric: string) => void;
  // Available metric keys (from the BE catalog); falls back to the FE default order.
  metricKeys?: string[];
  // "pills" = admin horizontal pill row; "dropdown" = agent single Select.
  variant?: "pills" | "dropdown";
}

const KpiMetricSelector = ({
  metric,
  onMetricChange,
  metricKeys,
  variant = "pills",
}: KpiMetricSelectorProps) => {
  const { t } = useTranslation();

  const keys = useMemo<string[]>(
    () => (metricKeys && metricKeys.length > 0 ? metricKeys : (KPI_METRIC_ORDER as string[])),
    [metricKeys],
  );

  const options = useMemo(
    () => keys.map((key) => ({ value: key, label: t(getMetricLabelKey(key)) })),
    [keys, t],
  );

  if (variant === "dropdown") {
    return (
      <Select
        className={classes.metricSelect}
        data={options}
        value={metric}
        onChange={(value) => value && onMetricChange(value)}
        allowDeselect={false}
        mb={0}
      />
    );
  }

  return (
    <Group gap={10} className={classes.metricPills}>
      {keys.map((key) => {
        const active = key === metric;
        return (
          <UnstyledButton
            key={key}
            className={`${classes.metricPill} ${active ? classes.metricPillActive : ""}`}
            onClick={() => onMetricChange(key)}
          >
            {t(getMetricLabelKey(key))}
          </UnstyledButton>
        );
      })}
    </Group>
  );
};

export default KpiMetricSelector;
